<template>
  <!-- prettier-ignore -->
  <div class="doctors">
    <Form.Form
      v-if="formActive"
      v-unblu-captured
      class="doctors-form form"
      name="hq_doctors"
      :validator="v"
      @submit="save"
    >
      <div :class="{'form-inline': !hasCountry, 'form-rows' : hasCountry}">
        <Form.Input
          v-model="formData.name"
          class="form__input"
          name="name"
          theme="dark"
          :placeholder="t(`healthquestion.${$props.hqId}.form.doctors.doctor`)"
          :title="t(`healthquestion.${$props.hqId}.form.doctors.doctor`)"
          :validate="v.name"
        />

        <Form.Input
          v-model="formData.street"
          class="form__input"
          name="street"
          theme="dark"
          :placeholder="t('person.street')"
          :title="t('person.street')"
          :validate="v.street"
        />

        <Form.Input
          v-model="formData.town"
          class="form__input"
          name="town"
          theme="dark"
          :placeholder="t('person.town')"
          :title="t('person.town')"
          :validate="v.town"
        />

        <Form.Dropdown
          v-if="hasCountry"
          v-model="formData.country"
          class="form__input"
          name="nationality"
          required
          theme="dark"
          :options="nationalities"
          :title="t('person.country')"
          :validate="v.country"
        >
        </Form.Dropdown>
      </div>

      <div class="form_action">
        <Form.Button
          :plain="!$props.inline"
          :secondary="$props.inline"
          tiny
          @click="cancel"
        >
          {{ t('form.cancel') }}
        </Form.Button>

        <Form.Button
          :plain="!$props.inline"
          :primary="$props.inline"
          tiny
          type="button"
          @click="save"
        >
          {{ t('form.apply') }}
        </Form.Button>
      </div>
    </Form.Form>

    <table :class="{'doctors-table': true, table: true, 'table--disabled': formActive}">
      <tbody>
      <tr
        v-for="doctor in items"
        :key="doctor.doctorId"
        class="table-doctor"
      >
        <td>
          <Form.Checkbox
            name="linked"
            :model-value="Object.values($props.modelValue).includes(doctor.doctorId)"
            @update:modelValue="toggle(doctor.doctorId)"
          />
        </td>
        <td v-unblu-captured>{{ doctor.name }}, {{ doctor.street }}, {{ doctor.town }} {{ doctor.country }}</td>
        <td>
          <Form.Button
            v-if="!doctor.zsrNumber"
            plain
            tiny
            @click="edit(doctor.doctorId)"
          >
            {{ t('screen.healthquestions.edit') }}
          </Form.Button>
        </td>
      </tr>
      </tbody>
    </table>

    <Form.Button
      v-if="$props.limit > items.length"
      plain
      tiny
      @click="add"
    >
      {{ t(`healthquestion.${$props.hqId}.form.doctors.add`) }}
    </Form.Button>

    <div class="doctors__hint">{{ autoHint }}</div>
  </div>
</template>

<script setup>
import { computed, reactive, ref, onMounted, nextTick } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import { cloneDeep } from 'lodash'

import * as Form from '@/components/Form'
import { getCountries } from '@/config/enums'
import { COUNTRY_CODES_SWISS_AND_NEIGHBOURS } from '@/config/constants'
import useHealthquestion from '@/components/Healthquestion/useHealthquestion'
import { ReactivityUtil } from '@/utils/Reactivity'
import { useI18n } from 'vue-i18n'
import useVuelidate from '@vuelidate/core'
import useBasket from '@/hooks/useBasket'

import { required } from '@vuelidate/validators'

// Hooks
const { basket } = useBasket()
const { tmpDoctors } = useHealthquestion()
const { t, locale } = useI18n()

// Init
const emit = defineEmits(['updatedDoctors'])
const props = defineProps({
  hqId: {
    type: String,
    required: true,
  },

  // if this flag is set to false, country CH will be saved as default
  hasCountry: {
    type: Boolean,
    default: false,
  },

  inline: {
    type: Boolean,
    default: false,
  },

  limit: {
    type: Number,
    default: 10,
  },

  validate: {
    type: Object,
    default: () => {},
  },

  modelValue: {
    type: Array,
    default: () => [],
  },
})

// Data
const initialForm = {
  doctorId: null,
  name: null,
  country: null,
  street: null,
  town: null,
}
const formData = reactive(initialForm)
const formActive = ref(false)

// Computed
const autoHint = computed(() => {
  const errors = Object.keys(props.validate?.$params ?? {}).filter(error => !props.validate[error])

  if (invalid.value && errors.length > 0) {
    return t(`form.error.${errors[0]}`)
  } else {
    return ''
  }
})

const invalid = computed(() => {
  return props.validate?.$invalid && props.validate?.$error
})

const nationalities = computed(() => {
  if (!props.hasCountry) {
    return null
  }

  return getCountries(locale.value, COUNTRY_CODES_SWISS_AND_NEIGHBOURS).map(country => {
    return {
      value: country.code.toUpperCase(),
      text: country.text,
    }
  })
})

const items = computed(() => {
  const data = cloneDeep(tmpDoctors).concat(cloneDeep(basket.doctors))

  // return data
  return data
    .filter((value, index, self) => {
      // Remove edited basket doctors redefined by tmpDoctors
      return self.findIndex(s => s.doctorId === value.doctorId) === index
    })
    .sort((doc1, doc2) => (doc1.name > doc2.name ? 1 : -1))
})

const validations = computed(() => {
  const rules = {}

  if (formActive.value) {
    rules.name = { required }
    rules.street = { required }
    rules.town = { required }
  }

  if (formActive.value && props.hasCountry) {
    rules.country = {
      required,
    }
  }

  return rules
})

// @TODO define correct order in code structure respecting dependencies
const v = useVuelidate(validations, formData, { $lazy: true })
defineExpose({ v, formActive, save })

// Methods
function add() {
  formActive.value = true
}

function cancel() {
  ReactivityUtil.reAssign(formData, initialForm)
  nextTick(() => {
    v.value.$reset()
  })
  formActive.value = false
}

function edit(id) {
  const doctor = items.value.find(d => d.doctorId === id)
  ReactivityUtil.reAssign(formData, cloneDeep(doctor))
  formActive.value = true
}

function save() {
  v.value.$touch()

  // if anyError => abort
  if (v.value.$error) return

  const doctor = cloneDeep(formData)

  // existing doc => update
  if (doctor.doctorId) {
    let index = tmpDoctors.findIndex(d => d.doctorId === doctor.doctorId)
    index = index >= 0 ? index : 0
    tmpDoctors[index] = doctor

    if (!props.modelValue.includes(doctor.doctorId)) {
      // eslint-disable-next-line vue/no-mutating-props
      props.modelValue.push(doctor.doctorId)
    }

    // new doc => create
  } else {
    const id = uuidv4()
    doctor.doctorId = id

    // If country can not be selected, set CH as default
    if (!props.hasCountry) {
      doctor.country = 'CH'
    }
    doctor.new = true

    // add doc to tmp
    tmpDoctors.push(doctor)

    // add doc to list
    toggle(id)
  }

  // restore form
  cancel()
  emit('updatedDoctors', items.value)
}

/**
 * toggle selection of doctor
 */
function toggle(id) {
  if (props.modelValue.includes(id)) {
    // eslint-disable-next-line vue/no-mutating-props
    props.modelValue.splice(
      props.modelValue.findIndex(doctorId => doctorId === id),
      1
    )
  } else {
    // eslint-disable-next-line vue/no-mutating-props
    props.modelValue.push(id)
  }
}

// Lifecycle hooks
onMounted(() => {
  if (basket.doctors.length === 0) {
    add()
  }
})
</script>

<style name="mobile" scoped lang="postcss">
.doctors {
  &-form.form {
    display: flex;
    flex-direction: column;

    .form-inline,
    .form-rows {
      display: flex;
      flex-direction: column;
      margin-bottom: 10px;
      grid-gap: 5px;
    }

    .form__input {
    }

    .form_action {
      display: flex;
      flex-direction: row;
      align-self: flex-end;
      grid-gap: 5px;
    }
  }

  &-table.table {
    width: 100%;
    user-select: none;

    transition: all 0.3s ease-in-out;

    &--disabled {
      opacity: 0.5;
      transform: scale(0.85);
      pointer-events: none;
    }

    .table-doctor {
      td {
        &:first-child {
          width: 25px;
        }

        &:nth-child(2) {
          width: 80%;
        }

        &:last-child {
          width: 20%;
          text-align: right;
        }
      }
    }
  }

  & :deep(button.plain) {
    color: var(--c-original-green);
  }

  &__hint {
    color: var(--c-error);
  }
}
</style>

<style name="desktop" scoped lang="postcss">
@media (--md) {
  .doctors {
    &-form.form {
      .form-inline {
        flex-direction: row;

        .form__input {
          &:first-child {
            flex: 1 1 40%;
            margin-right: 5px;
          }

          &:nth-child(2) {
            margin: 0 5px;
            flex: 1 1 30%;
          }

          &:last-child {
            margin-left: 5px;
            flex: 1 1 30%;
          }
        }
      }

      .form-rows {
        display: grid;
        grid-template-rows: 1fr 1fr;
        grid-template-columns: 1fr 1fr;
      }

      .form_action {
        button:last-child {
          margin-left: 20px;
        }
      }
    }

    &__hint {
      font-size: 15px;
      line-height: 20px;
      padding: 0 0 0 10px;
    }
  }
}
</style>
